import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { Tooltip } from "react-tooltip";
import TabNavigation from "../../components/common/TabNavigation";
import { audiencesService } from "../../services/audiences";
import { patternsService } from "../../services/patterns";
import { parsePaginatedResponse } from "../../utils/pagination";
import { formatNumber as defaultFormatNumber } from "../../components/patterns/utils";
import { PATTERN_TYPE_DESCRIPTIONS } from "../../constants/patternTypes";
import PatternOverview from "./overview";
import PatternStats from "./stats";
import Adapt from "./adapt";
import PatternExamples from "./examples";

const PATTERN_TABS = [
  "overview",
  "examples",
  "stats",
  "scripts",
];

/**
 * Unified pattern detail. Callers must pass baseUrl and onClose.
 * - baseUrl: e.g. /audience/x/patterns/Format/id/ or /results/patterns/hash/
 * - onClose: called when user closes
 * - pattern: use directly when provided (e.g. results); else patterns + patternId from URL (e.g. audience)
 */
const PatternDetail = ({
  baseUrl,
  onClose,
  pattern: patternProp,
  patterns: patternsProp,
  audience,
  patternRequestId,
  baseline = null,
  meta = null,
  fetchParams = null,
  audienceByHash = null,
  formatNumber,
  patternTypeLabel,
  patternType,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { patternId: patternIdParam } = useParams();
  const formatNumberFn = formatNumber ?? defaultFormatNumber;
  const baseUrlNorm = baseUrl?.endsWith("/") ? baseUrl : `${baseUrl || ""}/`;

  const [patternVideoRows, setPatternVideoRows] = useState([]);
  const [loadingVideos, setLoadingVideos] = useState(false);
  const [fetchedData, setFetchedData] = useState(null);
  const [fetchError, setFetchError] = useState(null);
  const [loadingFetch, setLoadingFetch] = useState(false);

  // Resolve pattern: from prop, from patterns list by URL param, or from fetched state
  const patternFromList =
    patternsProp && patternIdParam ? patternsProp.find((p) => p.id === patternIdParam) ?? null : null;
  const pattern = patternProp ?? patternFromList ?? fetchedData?.pattern ?? null;
  const resolvedBaseline = baseline ?? fetchedData?.baseline ?? null;
  const resolvedMeta = meta ?? fetchedData?.meta ?? null;
  const resolvedRequestId = patternRequestId ?? fetchedData?.patternRequestId ?? null;
  const resolvedAudience = audience ?? (fetchedData?.audienceHash && audienceByHash?.[fetchedData.audienceHash]) ?? audience ?? null;

  const pathnameNorm = (location.pathname || "").endsWith("/") ? location.pathname : `${location.pathname || ""}/`;
  const tabSegment = pathnameNorm.replace(baseUrlNorm, "").replace(/^\/|\/$/g, "").split("/")[0];
  const currentTab = PATTERN_TABS.includes(tabSegment) ? tabSegment : "overview";
  const searchSuffix = location.search || "";

  useEffect(() => {
    if (!PATTERN_TABS.includes(tabSegment)) {
      navigate(`${baseUrlNorm}overview${searchSuffix}`, { replace: true });
    }
  }, [tabSegment, baseUrlNorm, navigate, searchSuffix]);

  // Optional fetch when pattern is missing and fetchParams provided
  useEffect(() => {
    if (pattern != null || !fetchParams?.audience_hash || !fetchParams?.pattern_key) return;
    if (!fetchParams?.analysis_id && !fetchParams?.pattern_type) return;
    let alive = true;
    setLoadingFetch(true);
    setFetchError(null);
    const listParams = {
      audience_hash: fetchParams.audience_hash,
      regenerate: false,
    };
    if (fetchParams.analysis_id) {
      listParams.analysis_id = fetchParams.analysis_id;
      if (fetchParams.pattern_type) {
        listParams.pattern_type = fetchParams.pattern_type;
      }
    } else {
      listParams.pattern_type = fetchParams.pattern_type;
      listParams.upload_timeframe = fetchParams.upload_timeframe ?? "month";
      if (fetchParams.creator_hash) {
        listParams.creator_hash = fetchParams.creator_hash;
      }
    }
    patternsService.getPatterns(
      listParams,
      (response) => {
        if (!alive) return;
        const payload = response?.data ?? {};
        const list = Array.isArray(payload.patterns) ? payload.patterns : [];
        const selected = list.find(
          (p) => String(p.id || "").trim() === String(fetchParams.pattern_key || "").trim()
        );
        if (!selected) {
          setFetchError("Could not load this pattern.");
          setLoadingFetch(false);
          return;
        }
        const aud = audienceByHash?.[fetchParams.audience_hash] ?? null;
        setFetchedData({
          pattern: selected,
          baseline: payload?.meta?.videos ?? null,
          meta: payload?.meta ?? null,
          patternRequestId: payload?.pattern_request_id ?? null,
          audienceHash: fetchParams.audience_hash,
          patternType: payload?.pattern_type || fetchParams.pattern_type || selected?.pattern_type,
        });
        setLoadingFetch(false);
      },
      () => {
        if (!alive) return;
        setFetchError("Could not open this pattern.");
        setLoadingFetch(false);
      }
    );
    return () => {
      alive = false;
    };
  }, [
    pattern,
    fetchParams?.audience_hash,
    fetchParams?.pattern_type,
    fetchParams?.pattern_key,
    fetchParams?.analysis_id,
    fetchParams?.upload_timeframe,
    fetchParams?.creator_hash,
    audienceByHash,
  ]);

  // Fetch pattern videos
  useEffect(() => {
    if (!resolvedAudience?.hash || resolvedRequestId == null || !pattern?.id) return;
    setLoadingVideos(true);
    audiencesService.getAudienceVideos(
      {
        audience_hash: resolvedAudience.hash,
        pattern_request_id: resolvedRequestId,
        pattern_id: pattern.id,
        page_size: 1000,
      },
      (response) => {
        const { results } = parsePaginatedResponse(response);
        setPatternVideoRows(results.filter((item) => item?.video));
        setLoadingVideos(false);
      },
      () => setLoadingVideos(false)
    );
  }, [resolvedAudience?.hash, resolvedRequestId, pattern?.id]);

  const patternSubtitle = useMemo(() => {
    const videoCount = pattern?.stats?.video_count;
    const composite = pattern?.creator_comparison?.composite_outlier;
    const label = (patternTypeLabel || "Pattern").trim();
    const parts = [];
    if (composite != null && typeof composite === "number" && !Number.isNaN(composite)) {
      const pct = Math.round((composite - 1) * 100);
      parts.push(`${pct >= 0 ? "+" : ""}${pct}% creator boost`);
    }
    if (videoCount != null) {
      parts.push(`(${videoCount} video${videoCount === 1 ? "" : "s"})`);
    }
    if (parts.length === 0) return videoCount != null ? `${label} (${videoCount} videos)` : label;
    return `${label} pattern for ${parts.join(" ")}`;
  }, [pattern?.stats?.video_count, pattern?.creator_comparison?.composite_outlier, patternTypeLabel]);

  const patternTypeTooltip = PATTERN_TYPE_DESCRIPTIONS[patternType]?.tooltip;

  const tabItems = useMemo(
    () => [
      { id: "overview", label: "Overview", to: `${baseUrlNorm}overview${searchSuffix}`, active: currentTab === "overview" },
      { id: "examples", label: "Examples", to: `${baseUrlNorm}examples${searchSuffix}`, active: currentTab === "examples" },
      // { id: "stats", label: "Stats", to: `${baseUrlNorm}stats${searchSuffix}`, active: currentTab === "stats" },
      { id: "scripts", label: "Brand Scripts", to: `${baseUrlNorm}scripts${searchSuffix}`, active: currentTab === "scripts" },
    ],
    [baseUrlNorm, currentTab, searchSuffix]
  );

  if (loadingFetch) {
    return (
      <div className="flex items-center justify-center py-16 text-theme-text">
        <span className="text-sm opacity-80">Loading pattern...</span>
      </div>
    );
  }
  if (fetchError) {
    return (
      <div className="rounded-lg border border-theme-secondary bg-theme-secondary p-4 text-theme-text opacity-80">
        {fetchError}
      </div>
    );
  }
  if (!pattern) return null;

  return (
    <div className="flex flex-col min-h-0 flex-1">
      <div className="flex-shrink-0 px-6 pt-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex flex-col gap-0.5">
            <div className="flex items-center gap-1.5">
              <h3 className="text-lg font-semibold text-theme-text">
                {pattern?.name ?? pattern?.id ?? "Pattern"}
              </h3>
              {patternTypeTooltip ? (
                <span
                  className="shrink-0 cursor-help text-theme-text opacity-40 hover:opacity-70"
                  data-tooltip-id="pattern-type-tip"
                  data-tooltip-content={patternTypeTooltip}
                  aria-label="What this pattern type means"
                >
                  <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden>
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a.75.75 0 000 1.5h.253a.25.25 0 01.244.304l-.459 2.066A1.75 1.75 0 0010.747 15H11a.75.75 0 000-1.5h-.253a.25.25 0 01-.244-.304l.459-2.066A1.75 1.75 0 009.253 9H9z" clipRule="evenodd" />
                  </svg>
                </span>
              ) : null}
            </div>
            <span className="text-sm text-theme-text opacity-60">
              {patternSubtitle}
            </span>
          </div>
          <div className="flex items-center gap-1">
            {currentTab !== "scripts" && baseUrlNorm && (
              <Link
                to={`${baseUrlNorm}scripts${searchSuffix}`}
                className="px-4 py-2 rounded-lg bg-theme-primary text-theme-button text-sm font-semibold hover:opacity-85"
              >
                Create Scripts
              </Link>
            )}
            <button
              onClick={onClose}
              className="text-theme-text opacity-70 hover:opacity-100"
              aria-label="Close"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="border-b border-theme-secondary">
            <TabNavigation items={tabItems} />
          </div>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto">
        <div className="px-6 pt-4 pb-6">
        {currentTab === "overview" && (
          <PatternOverview
            pattern={pattern}
            baseline={resolvedBaseline}
            meta={resolvedMeta}
            audience={resolvedAudience}
            patternRequestId={resolvedRequestId}
            formatNumber={formatNumberFn}
            patternVideoRows={patternVideoRows}
            loadingVideos={loadingVideos}
            baseUrl={baseUrlNorm}
          />
        )}
        {currentTab === "stats" && (
          <PatternStats
            pattern={pattern}
            baseline={resolvedBaseline}
            formatNumber={formatNumberFn}
            patternVideoRows={patternVideoRows}
            loadingVideos={loadingVideos}
          />
        )}
        {currentTab === "scripts" && (
          <Adapt
            baseUrl={baseUrlNorm ? `${baseUrlNorm}scripts` : "scripts"}
            pattern={pattern}
            patternTypeLabel={patternTypeLabel}
            patternType={patternType}
            audience={resolvedAudience}
            patternRequestId={resolvedRequestId}
            onClose={onClose}
          />
        )}
        {currentTab === "examples" && (
          <PatternExamples
            patternVideoRows={patternVideoRows}
            loadingVideos={loadingVideos}
            formatNumber={formatNumberFn}
            baseUrl={baseUrlNorm}
          />
        )}
        </div>
      </div>

      <Tooltip
        id="pattern-type-tip"
        place="top"
        className="!max-w-xs !text-xs !leading-relaxed"
      />
    </div>
  );
};

export default PatternDetail;
